import { STATUS_KEYS } from './scanAdapter';

/**
 * Scans list state in the URL: ?status=all&search=shell&userId=POM17529&placeId=ChIJ…&page=2&limit=50.
 * Values are validated and fall back to defaults, so a hand-edited URL can't 400.
 * The detail page keeps this query string so "back" returns to the same list.
 */

export const PAGE_SIZES = [10, 20, 50, 100];
export const MIN_SEARCH_LENGTH = 2;
export const MAX_SEARCH_LENGTH = 100;
export const DEFAULT_PARAMS = Object.freeze({ status: 'pending', page: 1, limit: 20, search: '', userId: '', placeId: '' });

const intIn = (raw, min, max) => (raw !== null && /^\d+$/.test(raw) && +raw >= min && +raw <= max ? +raw : null);
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const PLACE_RE = /^[A-Za-z0-9_-]{1,300}$/;

export function parseScansParams(searchParams) {
  const invalid = [];
  const get = (key) => searchParams.get(key);
  const pick = (key, value) => {
    if (get(key) === null) return DEFAULT_PARAMS[key];
    if (value === null) {
      invalid.push(key);
      return DEFAULT_PARAMS[key];
    }
    return value;
  };
  const rawSearch = get('search');
  const search = (rawSearch ?? '').trim().slice(0, MAX_SEARCH_LENGTH);
  if (rawSearch !== null && search.length < MIN_SEARCH_LENGTH) invalid.push('search');
  const status = get('status');

  return {
    params: {
      status: pick('status', ['all', ...STATUS_KEYS].includes(status) ? status : null),
      page: pick('page', intIn(get('page'), 1, Number.MAX_SAFE_INTEGER)),
      limit: pick('limit', intIn(get('limit'), 1, 100)),
      search: search.length >= MIN_SEARCH_LENGTH ? search : '',
      userId: pick('userId', ID_RE.test(get('userId') ?? '') ? get('userId') : null),
      placeId: pick('placeId', PLACE_RE.test(get('placeId') ?? '') ? get('placeId') : null),
    },
    invalid,
  };
}

/** URL query string; defaults are left out. */
export function toQueryString(params) {
  const qs = new URLSearchParams();
  for (const key of ['status', 'search', 'userId', 'placeId', 'page', 'limit']) {
    if (params[key] !== DEFAULT_PARAMS[key] && params[key] !== '') qs.set(key, String(params[key]));
  }
  return qs.toString();
}

/** Any change other than the page itself goes back to page 1. */
export function updateParams(current, changes) {
  const next = { ...current, ...changes };
  if (!Object.keys(changes).every((k) => k === 'page')) next.page = 'page' in changes ? changes.page : 1;
  return next;
}

/** "?status=all&page=2" → list URL, keeping the filters ("" → "/scan-details"). */
export const listPath = (search = '') => `/scan-details${search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : ''}`;
/** Detail URL that carries the list's query string. */
export const detailPath = (id, search = '') => `/scan-details/${id}${search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : ''}`;
